/**
 * Environment configuration
 * Each app has its own .env file with VITE_* variables
 */

export interface Env {
  // Required
  API_BASE_URL: string

  // Optional
  GRAPHQL_BASE_URL?: string
  AGORA_APP_ID?: string
  RECORDING_BUCKET?: string
  WEBSOCKET_PROXY_URL?: string

  // Feature flags
  IS_UNSW: boolean
  SENTRY_ENABLED: boolean
  ENABLE_ANALYTICS: boolean

  // Chat
  CRISP_WEBSITE_ID?: string

  // Analytics
  POSTHOG_KEY?: string
  POSTHOG_HOST?: string
  HOTJAR_ID?: string
  HOTJAR_SNIPPET_VERSION?: string

  // Mode
  MODE: 'development' | 'production' | 'test'
  PROD: boolean
  DEV: boolean
}

let cachedEnv: Env | null = null

/**
 * Get environment variables
 * Reads from Vite's import.meta.env
 */
export function getEnv(): Env {
  if (cachedEnv) return cachedEnv

  cachedEnv = {
    // Required
    API_BASE_URL: import.meta.env.VITE_API_BASE_URL ?? '',

    // Optional
    GRAPHQL_BASE_URL: import.meta.env.VITE_GRAPHQL_BASE_URL,
    AGORA_APP_ID: import.meta.env.VITE_AGORA_APP_ID,
    RECORDING_BUCKET: import.meta.env.VITE_RECORDING_BUCKET,
    WEBSOCKET_PROXY_URL: import.meta.env.VITE_WEBSOCKET_PROXY_URL,

    // Feature flags
    IS_UNSW: import.meta.env.VITE_IS_UNSW === 'true',
    SENTRY_ENABLED: import.meta.env.VITE_SENTRY === 'true',
    ENABLE_ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',

    // Chat
    CRISP_WEBSITE_ID: import.meta.env.VITE_CRISP_WEBSITE_ID,

    // Analytics
    POSTHOG_KEY: import.meta.env.VITE_POSTHOG_KEY,
    POSTHOG_HOST: import.meta.env.VITE_POSTHOG_HOST,
    HOTJAR_ID: import.meta.env.VITE_HOTJAR_ID,
    HOTJAR_SNIPPET_VERSION: import.meta.env.VITE_HOTJAR_SNIPPET_VERSION,

    // Mode
    MODE: import.meta.env.MODE as 'development' | 'production' | 'test',
    PROD: import.meta.env.MODE === 'production',
    DEV: import.meta.env.MODE === 'development',
  }

  // Validate required variables in development
  if (cachedEnv.DEV && !cachedEnv.API_BASE_URL) {
    console.warn('[env] VITE_API_BASE_URL is not set. API calls will fail.')
  }

  return cachedEnv
}

/**
 * Reset cached env (useful for testing)
 */
export function resetEnvCache(): void {
  cachedEnv = null
}
